import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { formatCpf } from '@utils/cpf';
import { formatBirthdate, isValidBirthdate } from '@utils/date';
import { PHONE_REGEX, formatPhone } from '@utils/phone';

import { AddressSection } from './AddressSection';
import { EMAIL_REGEX, buildDefaultValues, toProfileUpdate, type ProfileFormValues } from './profileForm.helpers';

import type { Profile, ProfileUpdate } from '@services/profile/profile.types';

type FormStatus = 'idle' | 'loading' | 'success' | 'error';

export interface ProfileFormProps {
  initialProfile: Profile;
  onSubmit: (update: ProfileUpdate) => Promise<void>;
  onBack: () => void;
}

export const ProfileForm = ({ initialProfile, onSubmit, onBack }: ProfileFormProps) => {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProfileFormValues>({
    defaultValues: buildDefaultValues(initialProfile),
  });

  const submit = async (values: ProfileFormValues) => {
    setStatus('loading');
    setServerError(null);

    try {
      await onSubmit(toProfileUpdate(values));
      reset(values);
      setStatus('success');
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Erro ao salvar. Tente novamente.');
      setStatus('error');
    }
  };

  const closeFeedback = () => setStatus('idle');

  const birthdateField = register('birthdate', {
    validate: (value) => !value || isValidBirthdate(value) || 'Data inválida (use dd/mm/aaaa)',
  });

  const phoneField = register('phone', {
    validate: (value) => !value || PHONE_REGEX.test(value) || 'Telefone inválido (use (99) 99999-9999)',
  });

  return (
    <Box>
      <Button variant="text" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2, color: 'text.secondary' }}>
        Nova busca
      </Button>

      <Alert severity="info" icon={<LockOutlinedIcon fontSize="inherit" />} sx={{ mb: 3 }}>
        <AlertTitle>Edição de dados cadastrais</AlertTitle>
        Apenas os dados cadastrais abaixo podem ser alterados. Os <strong>dados de identidade (Keycloak)</strong> são
        somente leitura e não podem ser editados por esta tela.
      </Alert>

      <Box
        component="form"
        onSubmit={handleSubmit(submit)}
        noValidate
        aria-label="Editar perfil"
        sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
      >
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Dados Pessoais
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <TextField
              label="Nome Completo"
              required
              {...register('name', { required: 'Nome é obrigatório' })}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
            <TextField label="Nome Social" {...register('social_name')} />
            <TextField
              label="CPF"
              value={formatCpf(initialProfile.cpf)}
              disabled
              helperText="Somente leitura"
              inputProps={{ readOnly: true, 'aria-readonly': 'true' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon fontSize="small" color="disabled" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Data de Nascimento"
              inputProps={{ inputMode: 'numeric', maxLength: 10 }}
              {...birthdateField}
              onChange={(e) => {
                e.target.value = formatBirthdate(e.target.value);
                birthdateField.onChange(e);
              }}
              error={!!errors.birthdate}
              helperText={errors.birthdate?.message ?? 'dd/mm/aaaa'}
            />
            <TextField label="Nome da mãe" sx={{ gridColumn: { sm: 'span 2' } }} {...register('mother_name')} />
          </Box>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Contato
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <TextField
              label="Email"
              type="email"
              {...register('email', {
                validate: (value) => !value || EMAIL_REGEX.test(value) || 'E-mail inválido',
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <TextField
              label="Telefone"
              placeholder="(99) 99999-9999"
              inputProps={{ inputMode: 'numeric', maxLength: 15 }}
              {...phoneField}
              onChange={(e) => {
                e.target.value = formatPhone(e.target.value);
                phoneField.onChange(e);
              }}
              error={!!errors.phone}
              helperText={errors.phone?.message}
            />
          </Box>
        </Paper>

        <AddressSection register={register} errors={errors} setValue={setValue} watch={watch} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={onBack}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={status === 'loading' || !isDirty}
            startIcon={status === 'loading' ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          >
            {status === 'loading' ? 'Salvando...' : 'Salvar alterações'}
          </Button>
        </Box>
      </Box>

      <Dialog
        open={status === 'success'}
        onClose={closeFeedback}
        aria-labelledby="feedback-sucesso-titulo"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="feedback-sucesso-titulo" sx={{ textAlign: 'center', pt: 4 }}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, display: 'block', mx: 'auto', mb: 1 }} />
          Perfil salvo com sucesso!
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center' }}>As alterações cadastrais foram salvas.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={closeFeedback} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={status === 'error' && !!serverError}
        onClose={closeFeedback}
        aria-labelledby="feedback-erro-titulo"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="feedback-erro-titulo" sx={{ textAlign: 'center', pt: 4 }}>
          <ErrorOutlineIcon color="error" sx={{ fontSize: 56, display: 'block', mx: 'auto', mb: 1 }} />
          Não foi possível salvar
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center' }}>{serverError}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={closeFeedback} variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
